import { map, take } from "rxjs/operators";
import { FiatService } from "@main-services/currency/fiat.service";
import { Account, Fiat, Notification, PayoutAccount } from "@models";
import { Injectable } from "@angular/core";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { Router } from "@angular/router";
import { Helper, parseList } from "@helpers/functions";
import { debug } from "@helpers/debug";
import { MasterdataService } from "@core-services/masterdata.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { BehaviorSubject, combineLatest, Observable, zip } from "rxjs";
import { SocketService } from "@main-services/socket.service";
import { Tradable } from "@models/interfaces/tradable";
import { Index } from "@models/asset/index/index.model";
import { SegmentService } from "@main-services/segment.service";
import { truncateFixed } from "@helpers/utils";
import { toCurrencyByFiatId } from "@helpers/tradable-convert";
import { Term } from "@models/term";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class AccountService {
	private _accountUpdated: BehaviorSubject<Account>;
	private title: string = "Bitpanda";
	private fiat: Fiat = new Fiat();
	private _eidVerificationSuccessful: boolean = false;
	private isTCActive$ = this.launchDarkly.isActive$(LD_FLAG.TERMS_AND_CONDITIONS);

	public constructor(
		private fiatService: FiatService,
		private tradableListService: TradableListService,
		private apicallService: ApicallService,
		private infoService: InfoService,
		private payservice: PayAccountService,
		private router: Router,
		private masterdataService: MasterdataService,
		private profileFacade: NewProfileFacade,
		private translationService: TranslationService,
		private socketService: SocketService,
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService,
		private readonly launchDarkly: LaunchDarklyService
	) {
		this._accountUpdated = new BehaviorSubject<Account>(null);

		const ticker = Helper.getCookie("ticker");
		if ("" + ticker === "null") {
			Helper.setCookie("ticker", "false", true);
		}

		// Not this.setTimer() -> observable checks happen in setcycle
		this.setCycleCoinId(this.getCycleCoinId());

		this.translationService.languageUpdate$().subscribe((language) => {
			if (language === "esp") {
				this.accountUpdated.pipe(untilDestroyed(this)).subscribe((account) => {
					if (account && account._language === "esp") {
						account._language = "spa";
						this.update();
					}
				});
			} else {
				this.updateTitle(this.title);
			}
		});
	}

	public get accountUpdated(): Observable<Account> {
		return this._accountUpdated.asObservable();
	}

	public getDefaultFiat(): Fiat {
		return this.fiat;
	}

	public get account(): Account {
		return this._accountUpdated.getValue();
	}

	public set account(a: Account) {
		this.fiat = this.fiatService.getItemById(a.getFiatId());
		a.updateOriginalTerms();
		this._accountUpdated.next(a);
	}

	public update(): void {
		this.translationService.setLanguage(this.account.getLanguage());
		this.account.saving = true;
		const newData = Object.assign(new Account(), this.account, {
			terms: this.getSendTermsFormat()
		});
		const apicallOption = new ApicallOption(Account, newData);

		combineLatest({
			account: this.apicallService.post("user/settings", apicallOption).pipe(map((a) => a[0])),
			isTCActive: this.isTCActive$
		}).subscribe({
			next: ({ account, isTCActive }) => {
				this.account = account;
				if (!isTCActive && !this.acceptedTerms()) {
					this.segmentService.reset();
					this.rudderstackService.reset();
					this.router.navigateByUrl("/user/logout");
				}
				this.infoService.setInfo(this.translationService.translate("successfully_saved_settings"));
				this.updateCryptoCoinTitle();
				this.setTitle(this.title);
				this.fetchMembersData();
			},
			error: (error) => {
				// Note: the subject should not broadcast the error
				Helper.handleError(error, this.account);
			}
		});
	}

	public fetchMembersData(): void {
		const lang: string = this.translationService.getLanguage();
		const payoutprovidersOption = new ApicallOption();
		payoutprovidersOption.apiVersion = "v3";
		this.masterdataService.initMasterdata(lang).subscribe(() =>
			zip(
				this.apicallService.get("payoutproviders", payoutprovidersOption),
				this.apicallService.get("payoutaccounts"),
				this.apicallService.get("paymentoptions")
			)
				.pipe(take(1))
				.subscribe(([payoutProviders, payoutAccounts, paymentOptions]) => {
					this.masterdataService.setProviderFromData(payoutProviders, paymentOptions);
					this.masterdataService.checkLimits();
					this.payservice.list = parseList(payoutAccounts, PayoutAccount);
				})
		);
	}

	public socketInit(): void {
		this.socketService
			.joinRoom<Account>({
				name: "users",
				type: Account,
				isPrivate: true
			})
			.pipe(untilDestroyed(this))
			.subscribe((account: Account) => {
				if (account.getEvent() === "updated") {
					this._accountUpdated.next(account);
					this.profileFacade.fetchProfile();
					if (account.isDocumented()) {
						this._eidVerificationSuccessful = true;
					}
					this.fetchMembersData();
				}
			});
	}

	public get eidVerificationSuccessful(): boolean {
		return this._eidVerificationSuccessful;
	}

	public setCycleCoinId(c: string): void {
		Helper.setCookie("coin_id", c, true);
		combineLatest([
			this.accountUpdated.pipe(map((a: Account) => a != null)),
			this.tradableListService.getList().pipe(map((a: Tradable[]) => a.length > 0)),
			this.fiatService.getList().pipe(map((a: Fiat[]) => a.length > 0))
		])
			.pipe(
				untilDestroyed(this),
				map((a: boolean[]) => a.every((b: boolean) => b))
			)
			.subscribe((all: boolean) => {
				if (!all) {
					return;
				}

				let fiatId = "1";
				if (this.account) {
					fiatId = this.account.getFiatId();
				}
				this.fiat = this.fiatService.getItemById(fiatId);

				this.tradableListService
					.getList()
					.pipe(take(1))
					.subscribe(() => this.updateCryptoCoinTitle());
				this.updateCryptoCoinTitle();
			});
	}

	public getCycleCoinId(): string {
		let coinId = "" + Helper.getCookie("coin_id");
		if (isNaN(+coinId)) {
			coinId = "1";
			Helper.setCookie("coin_id", "1", true);
		}
		return coinId;
	}

	private updateCryptoCoinTitle(): void {
		if (this.ticker) {
			this.setTradablePrice(this.tradableListService.getItemById(this.getCycleCoinId()));
		}
	}

	public updateTicker(): void {
		Helper.setCookie("ticker", "" + !this.ticker, true);
		if (this.ticker) {
			this.updateCryptoCoinTitle();
		} else {
			this.updateTitle();
		}
	}

	public updateAdvanced(): void {
		Helper.setCookie("advanced", "" + !this.advanced, true);
	}

	public get ticker(): boolean {
		return Helper.getCookie("ticker") === "true";
	}

	public get advanced(): boolean {
		return Helper.getCookie("advanced") === "true";
	}

	public get tracker(): boolean {
		return this.account.getGoogleTracking();
	}

	public setTitle(title: string): void {
		this.title = title;
		if (debug) {
			console.log("[TICKER] " + title);
		}

		if (!this.ticker) {
			this.updateTitle(title);
		}
	}

	private updateTitle(s: string = this.title, i = 0): void {
		let trans = this.translationService.translate(s.split("#")[0]);
		if (trans.indexOf("_title") > -1 && i < 5) {
			TranslationService.LanguagesObservable().subscribe(() => this.updateTitle(s, ++i));
		} else {
			// We have some weird (new) routing without translation
			if (trans.endsWith("_title")) {
				trans = "Bitpanda";
			}
			document.title = trans;
		}
	}

	public setTradablePrice(tradable: Tradable, price: string = ""): void {
		if (!tradable) {
			return;
		}
		if (price === "") {
			let value: number = tradable.getAvgPrice();
			if (!(tradable instanceof Index)) {
				value = toCurrencyByFiatId(value, this.getDefaultFiat());
			}
			price = "" + truncateFixed(value, tradable.getPrecisionFiat());
		}
		const fiatSymbol: string = tradable instanceof Index ? "" : this.getDefaultFiat().getSymbol();
		this.updateTitle(tradable.getSymbol(false, true) + " @" + price + " " + fiatSymbol);
	}

	public getNotifications(): Notification[] {
		const result = [];
		result.push(new Notification("1", "transaction"));
		result.push(new Notification("2", "pricealert"));
		result.push(new Notification("3", "common"));
		return result;
	}

	public updateNotification(n: Notification): void {
		Helper.setCookie("notification_" + n.getId(), "" + n.getEnabled(), true);
	}

	public acceptedTerms(): boolean {
		return this.account.getTerms().every((t: Term) => t.hasSigned());
	}

	public showBest2TermsOnBestTrade(): boolean {
		return this.masterdataService.isBest2Enabled() && !this.account.isBest2TermsAccepted();
	}

	public acceptBest2Terms(termsAccepted: boolean): void {
		this.apicallService
			.post(
				"user/settings",
				new ApicallOption(null, {
					terms: {
						best2_terms: termsAccepted
					}
				})
			)
			.subscribe((error) => Helper.handleError(error));
	}

	public acceptedAdvancedTradingTerms(termsAccepted: boolean): void {
		this.apicallService
			.post(
				"user/settings",
				new ApicallOption(null, {
					terms: {
						advanced_trading: termsAccepted
					}
				})
			)
			.subscribe({
				next: (account: Account) => {
					if (account) {
						this.account.acceptAdvancedTrading();
					}
				},
				error: (error) => Helper.handleError(error)
			});
	}

	public getSendTermsFormat(): Object {
		const obj = {};
		this.account.getTermsForUpdate().forEach((term: Term) => {
			obj[term.getType()] = term.hasSigned();
		});
		return obj;
	}
}
